import {
  getRecordArray,
  isCatalogDataViewLabels,
  isCatalogSourceLabels,
  isRecord,
  parseComponentDataBinding,
} from "@/types/componentDataBinding";
import type {
  CatalogDataViewLabels,
  CatalogSourceLabels,
  ComponentDataBinding,
  ComponentDataBindingWriteTarget,
  ComponentDataWriteOperation,
  DataBindingFieldMapping,
  DataSourceRef,
  DataViewRef,
  StaticDataBindingRef,
} from "@/types/componentDataBinding";

const PROTOTYPE_KEYS = new Set(["__proto__", "constructor", "prototype"]);
const SENSITIVE_FIELD_PATTERN = /token|password|secret|authorization|header|credential/i;
const WRITE_OPERATIONS: ComponentDataWriteOperation[] = [
  "create",
  "update",
  "delete",
];

export interface AiBindingContextSummaryOptions {
  safeRuntimeRows?: unknown;
  relatedComponents?: AiBindingContextComponentInput[];
  sampleLimits?: Partial<AiBindingSampleLimits>;
}

export interface AiBindingContextComponentInput {
  id: string;
  type: string;
  name?: string;
  properties?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  safeRuntimeRows?: unknown;
}

export interface AiBindingSampleLimits {
  maxRows: number;
  maxFieldsPerRow: number;
  maxStringLength: number;
  maxSerializedSize: number;
}

export interface AiBindingSampleSummary {
  rows: Record<string, unknown>[];
  availableRowCount: number;
  includedRowCount: number;
  maxRows: number;
  maxFieldsPerRow: number;
  maxStringLength: number;
  maxSerializedSize: number;
  serializedSize: number;
  truncated: boolean;
  omittedFields: string[];
}

export type AiBindingSourceIdentitySummary =
  | {
      kind: "schema";
      dataOwnerOrganizationId: string;
      ownerType: DataSourceRef["ownerType"];
      ownerId: string;
      groupId: string;
      schemaId: string;
    }
  | {
      kind: DataViewRef["kind"];
      ownerType: DataViewRef["ownerType"];
      ownerId: string;
      viewOrganizationId: string;
      viewId: string;
    }
  | {
      kind: "static";
      adapterId: string;
    }
  | {
      kind: "unknown";
    };

export type AiBindingWriteTargetSummary =
  | {
      present: false;
    }
  | {
      present: true;
      target: AiBindingSourceIdentitySummary;
      readiness?: Partial<Record<ComponentDataWriteOperation, string>>;
    }
  | {
      present: true;
      kind: "federated-view-source";
      targetSourceId: string;
      view: AiBindingSourceIdentitySummary;
      source: AiBindingSourceIdentitySummary;
      readiness?: Partial<Record<ComponentDataWriteOperation, string>>;
    };

export interface AiRelatedBindingComponentSummary {
  id: string;
  type: string;
  name?: string;
  sourceKind: AiBindingSourceIdentitySummary["kind"];
}

export interface AiBindingContextSummary {
  componentId: string;
  componentType: string;
  componentName?: string;
  status: "bound" | "missing" | "reconnect" | "unknown";
  reconnectReason: "missing-source-scope" | "malformed-binding" | null;
  source: AiBindingSourceIdentitySummary;
  sourceLabels?: CatalogSourceLabels;
  viewLabels?: CatalogDataViewLabels;
  fieldMapping: DataBindingFieldMapping;
  writeTarget: AiBindingWriteTargetSummary;
  sample?: AiBindingSampleSummary;
  relatedComponents: AiRelatedBindingComponentSummary[];
}

export interface AiBindingInstructionSurface {
  phase: "11B";
  mode: "read-only-existing-source-binding";
  allowedComponentFamilies: string[];
  requiredSourceIdentities: {
    schema: {
      kind: "schema";
      requiredFields: string[];
    };
    dataView: {
      kinds: Array<"query-template" | "federated-view">;
      requiredFields: string[];
    };
  };
  clarifyWhen: string[];
  reject: string[];
  writeTargetClarification?: AiWriteTargetClarificationInstructionSurface;
}

export interface AiWriteTargetClarificationInstructionSurface {
  phase: "11C";
  mode: "write-target-clarification";
  allowedComponentFamilies: string[];
  requiredWriteTarget: {
    cardinality: "exactly-one";
    operations: ComponentDataWriteOperation[];
    authority: "operation-specific";
    schema: {
      kind: "schema";
      requiredFields: string[];
    };
    federatedViewSource: {
      kind: "federated-view-source";
      requiredFields: string[];
    };
  };
  clarifyWhen: string[];
  refuseWhen: string[];
  forbiddenFallbacks: string[];
  preserveBoundaries: string[];
}

export const PHASE_11B_BINDING_INSTRUCTIONS: AiBindingInstructionSurface = {
  phase: "11B",
  mode: "read-only-existing-source-binding",
  allowedComponentFamilies: [
    "chart",
    "table",
    "project-management",
  ],
  requiredSourceIdentities: {
    schema: {
      kind: "schema",
      requiredFields: [
        "dataOwnerOrganizationId",
        "ownerType",
        "ownerId",
        "groupId",
        "schemaId",
      ],
    },
    dataView: {
      kinds: ["query-template", "federated-view"],
      requiredFields: [
        "kind",
        "viewId",
        "ownerType",
        "ownerId",
        "viewOrganizationId",
      ],
    },
  },
  clarifyWhen: [
    "dataOwnerOrganizationId is missing or ambiguous",
    "viewOrganizationId is missing or ambiguous",
    "source or view scope is missing or ambiguous",
    "selected/current organization is the only scope signal",
  ],
  reject: [
    "bare viewId without ownerType, ownerId, and viewOrganizationId",
    "raw datasets, raw dataBinding internals, or full component records",
    "copying project-management records into component props",
    "record create, update, delete, write-target, or mutation commands",
  ],
};

export const PHASE_11C_WRITE_TARGET_INSTRUCTIONS: AiWriteTargetClarificationInstructionSurface = {
  phase: "11C",
  mode: "write-target-clarification",
  allowedComponentFamilies: [
    "project-management",
  ],
  requiredWriteTarget: {
    cardinality: "exactly-one",
    operations: WRITE_OPERATIONS,
    authority: "operation-specific",
    schema: {
      kind: "schema",
      requiredFields: [
        "dataOwnerOrganizationId",
        "ownerType",
        "ownerId",
        "groupId",
        "schemaId",
      ],
    },
    federatedViewSource: {
      kind: "federated-view-source",
      requiredFields: [
        "view.kind",
        "view.viewId",
        "view.ownerType",
        "view.ownerId",
        "view.viewOrganizationId",
        "targetSourceId",
        "source.dataOwnerOrganizationId",
        "source.ownerType",
        "source.ownerId",
        "source.groupId",
        "source.schemaId",
      ],
    },
  },
  clarifyWhen: [
    "more than one writable target is available",
    "write target is missing, malformed, or ambiguous",
    "requested create/update/delete operation authority is missing or unclear",
    "selected/current organization is the only scope signal",
  ],
  refuseWhen: [
    "no explicit write target is available",
    "target summary is read-only, reconnect, missing, unknown, or not-ready for the requested operation",
    "the command would create, update, or delete records through a read binding",
    "the user asks to guess a target from selected/current organization context",
  ],
  forbiddenFallbacks: [
    "selected organization",
    "current organization",
    "current page",
    "authenticated user organization",
    "source/view identity without writeTarget",
  ],
  preserveBoundaries: [
    "summarize only bounded write-target identity and operation readiness",
    "do not serialize raw datasets, PM records, tokens, auth headers, secrets, or credentials",
    "prompt guidance complements runtime preflight enforcement and is not the sole security boundary",
  ],
};

export const PHASE_11_DATA_BINDING_INSTRUCTIONS: AiBindingInstructionSurface = {
  ...PHASE_11B_BINDING_INSTRUCTIONS,
  writeTargetClarification: PHASE_11C_WRITE_TARGET_INSTRUCTIONS,
};

export function formatPhase11BBindingInstructionsForPrompt(): string {
  const schemaFields =
    PHASE_11B_BINDING_INSTRUCTIONS.requiredSourceIdentities.schema.requiredFields.join(
      "/",
    );
  const dataViewFields =
    PHASE_11B_BINDING_INSTRUCTIONS.requiredSourceIdentities.dataView.requiredFields.join(
      "/",
    );
  const dataViewKinds =
    PHASE_11B_BINDING_INSTRUCTIONS.requiredSourceIdentities.dataView.kinds.join(
      " or ",
    );

  return [
    "[BINDING RULES - Phase 11B read-only existing source/view binding]",
    "Bind chart/table/PM widgets only to existing explicit catalog sources or query-template/federated views from the available summaries.",
    `Schema sources require full identity: kind=schema plus ${schemaFields}.`,
    `${dataViewKinds} views require full identity: ${dataViewFields}; reject bare viewId.`,
    "Ask the user for clarification when dataOwnerOrganizationId, viewOrganizationId, source scope, or view scope is missing or ambiguous, or when selected/current org is the only scope signal.",
    "Do not serialize raw datasets, raw dataBinding internals, full component records, or PM records into component props.",
    "Do not create/update/delete records and do not add write-target instructions or mutation commands.",
  ].join("\n");
}

export function formatPhase11CWriteTargetInstructionsForPrompt(): string {
  const schemaFields =
    PHASE_11C_WRITE_TARGET_INSTRUCTIONS.requiredWriteTarget.schema.requiredFields.join(
      "/",
    );
  const federatedFields =
    PHASE_11C_WRITE_TARGET_INSTRUCTIONS.requiredWriteTarget.federatedViewSource.requiredFields.join(
      "/",
    );
  const operations =
    PHASE_11C_WRITE_TARGET_INSTRUCTIONS.requiredWriteTarget.operations.join(
      "/",
    );

  return [
    "[WRITE-TARGET RULES - Phase 11C PM write-target clarification]",
    "For PM record write commands, proceed only when the context provides exactly one explicit writable target.",
    `Direct schema write targets require ${schemaFields}.`,
    `Federated-view source write targets require ${federatedFields}.`,
    `Authority is operation-specific: the requested ${operations} operation must be ready for that exact target.`,
    "Ask for clarification when multiple targets exist, the target is missing/malformed/ambiguous, operation authority is unclear, or selected/current org is the only scope signal.",
    "Ask or refuse rather than guessing when the summary is read-only, absent, reconnect, missing, unknown, or not-ready for the requested operation.",
    "Never use selected/current organization, current page, authenticated user organization, source identity, or view identity as a write-target fallback.",
    "Keep summaries bounded: do not serialize raw datasets, PM records, raw dataBinding internals, tokens, auth headers, secrets, or credentials.",
    "These prompt/context rules complement runtime preflight enforcement; do not treat prompt text as the sole security boundary.",
  ].join("\n");
}

const DEFAULT_SAMPLE_LIMITS: AiBindingSampleLimits = {
  maxRows: 3,
  maxFieldsPerRow: 6,
  maxStringLength: 80,
  maxSerializedSize: 1200,
};

export function summarizeBindingContextForAi(
  component: AiBindingContextComponentInput,
  options: AiBindingContextSummaryOptions = {},
): AiBindingContextSummary {
  const bindingInput = component.properties?.dataBinding;
  const binding = parseComponentDataBinding(bindingInput);
  const safeRowsInput =
    options.safeRuntimeRows !== undefined
      ? options.safeRuntimeRows
      : component.safeRuntimeRows;

  if (binding === undefined) {
    return {
      componentId: component.id,
      componentType: component.type,
      componentName: component.name,
      status: bindingInput === undefined ? "missing" : "reconnect",
      reconnectReason:
        bindingInput === undefined ? "missing-source-scope" : "malformed-binding",
      source: { kind: "unknown" },
      fieldMapping: {},
      writeTarget: { present: false },
      relatedComponents: [],
    };
  }

  const metadata = getBindingMetadata(component);
  const writeReadiness = getWriteReadiness(metadata, component.properties);

  return {
    componentId: component.id,
    componentType: component.type,
    componentName: component.name,
    status: "bound",
    reconnectReason: null,
    source: summarizeSourceIdentity(binding.source),
    sourceLabels: findSourceLabels(metadata, component.properties),
    viewLabels: findViewLabels(metadata, component.properties),
    fieldMapping: binding.fieldMapping,
    writeTarget: summarizeWriteTarget(binding.writeTarget, writeReadiness),
    sample: summarizeSafeRows(safeRowsInput, options.sampleLimits),
    relatedComponents: findRelatedBindingComponents(
      component,
      options.relatedComponents ?? [],
    ),
  };
}

export function findRelatedBindingComponents(
  component: AiBindingContextComponentInput,
  candidates: AiBindingContextComponentInput[],
): AiRelatedBindingComponentSummary[] {
  const bindingKey = getExplicitBindingIdentityKey(component);
  if (bindingKey === undefined) {
    return [];
  }

  return candidates.flatMap((candidate) => {
    if (candidate.id === component.id) {
      return [];
    }

    const candidateKey = getExplicitBindingIdentityKey(candidate);
    if (candidateKey !== bindingKey) {
      return [];
    }

    const candidateBinding = parseComponentDataBinding(
      candidate.properties?.dataBinding,
    );
    if (candidateBinding === undefined) {
      return [];
    }

    return [
      {
        id: candidate.id,
        type: candidate.type,
        name: candidate.name,
        sourceKind: summarizeSourceIdentity(candidateBinding.source).kind,
      },
    ];
  });
}

function summarizeSafeRows(
  value: unknown,
  overrides: Partial<AiBindingSampleLimits> | undefined,
): AiBindingSampleSummary | undefined {
  const rows = getRecordArray(value);
  if (rows === undefined) {
    return undefined;
  }

  const limits = {
    ...DEFAULT_SAMPLE_LIMITS,
    ...overrides,
  };
  const sampleRows: Record<string, unknown>[] = [];
  const omittedFieldNames = new Set<string>();
  let truncated = rows.length > limits.maxRows;

  for (
    let rowIndex = 0;
    rowIndex < rows.length && sampleRows.length < limits.maxRows;
    rowIndex += 1
  ) {
    const row = rows[rowIndex];
    const sampleRow: Record<string, unknown> = {};
    let includedFields = 0;

    for (const [fieldName, fieldValue] of Object.entries(row)) {
      if (isUnsafeFieldName(fieldName)) {
        omittedFieldNames.add(fieldName);
        truncated = true;
        continue;
      }

      if (includedFields >= limits.maxFieldsPerRow) {
        omittedFieldNames.add(fieldName);
        truncated = true;
        continue;
      }

      const safeValue = summarizeSampleValue(fieldValue, limits.maxStringLength);
      const nextRow = {
        ...sampleRow,
        [fieldName]: safeValue,
      };
      const nextRows = [...sampleRows, nextRow];
      const nextSerializedSize = getSerializedSize(nextRows);

      if (nextSerializedSize > limits.maxSerializedSize) {
        omittedFieldNames.add(fieldName);
        truncated = true;
        continue;
      }

      sampleRow[fieldName] = safeValue;
      includedFields += 1;
      if (isStringTruncated(fieldValue, limits.maxStringLength)) {
        truncated = true;
      }
    }

    const nextRows = [...sampleRows, sampleRow];
    if (getSerializedSize(nextRows) > limits.maxSerializedSize) {
      truncated = true;
      break;
    }
    sampleRows.push(sampleRow);
  }

  return {
    rows: sampleRows,
    availableRowCount: rows.length,
    includedRowCount: sampleRows.length,
    maxRows: limits.maxRows,
    maxFieldsPerRow: limits.maxFieldsPerRow,
    maxStringLength: limits.maxStringLength,
    maxSerializedSize: limits.maxSerializedSize,
    serializedSize: getSerializedSize(sampleRows),
    truncated,
    omittedFields: Array.from(omittedFieldNames).sort(),
  };
}

function summarizeSampleValue(value: unknown, maxStringLength: number): unknown {
  if (typeof value === "string") {
    return value.length > maxStringLength
      ? value.slice(0, maxStringLength)
      : value;
  }

  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    value === null
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    return "[array]";
  }

  if (isRecord(value)) {
    return "[object]";
  }

  return String(value);
}

function isStringTruncated(value: unknown, maxStringLength: number): boolean {
  return typeof value === "string" && value.length > maxStringLength;
}

function getSerializedSize(value: Record<string, unknown>[]): number {
  return JSON.stringify(value).length;
}

function isUnsafeFieldName(fieldName: string): boolean {
  return PROTOTYPE_KEYS.has(fieldName) || SENSITIVE_FIELD_PATTERN.test(fieldName);
}

function summarizeSourceIdentity(
  source: ComponentDataBinding["source"],
): AiBindingSourceIdentitySummary {
  if (source.kind === "schema") {
    return summarizeDataSourceIdentity(source);
  }

  if (source.kind === "static") {
    return summarizeStaticIdentity(source);
  }

  return summarizeViewIdentity(source);
}

function summarizeDataSourceIdentity(
  source: DataSourceRef,
): AiBindingSourceIdentitySummary {
  return {
    kind: "schema",
    dataOwnerOrganizationId: source.dataOwnerOrganizationId,
    ownerType: source.ownerType,
    ownerId: source.ownerId,
    groupId: source.groupId,
    schemaId: source.schemaId,
  };
}

function summarizeStaticIdentity(
  source: StaticDataBindingRef,
): AiBindingSourceIdentitySummary {
  return {
    kind: "static",
    adapterId: source.adapterId,
  };
}

function summarizeViewIdentity(
  source: DataViewRef,
): AiBindingSourceIdentitySummary {
  return {
    kind: source.kind,
    ownerType: source.ownerType,
    ownerId: source.ownerId,
    viewOrganizationId: source.viewOrganizationId,
    viewId: source.viewId,
  };
}

function summarizeWriteTarget(
  writeTarget: ComponentDataBindingWriteTarget | undefined,
  readiness: Partial<Record<ComponentDataWriteOperation, string>> | undefined,
): AiBindingWriteTargetSummary {
  if (writeTarget === undefined) {
    return { present: false };
  }

  if (writeTarget.kind === "federated-view-source") {
    return appendReadiness(
      {
        present: true,
        kind: "federated-view-source",
        targetSourceId: writeTarget.targetSourceId,
        view: summarizeViewIdentity(writeTarget.view),
        source: summarizeDataSourceIdentity(writeTarget.source),
      },
      readiness,
    );
  }

  return appendReadiness(
    {
      present: true,
      target: summarizeDataSourceIdentity(writeTarget),
    },
    readiness,
  );
}

function appendReadiness<T extends AiBindingWriteTargetSummary>(
  summary: T,
  readiness: Partial<Record<ComponentDataWriteOperation, string>> | undefined,
): T {
  if (readiness === undefined || Object.keys(readiness).length === 0) {
    return summary;
  }

  return {
    ...summary,
    readiness,
  };
}

function getBindingMetadata(
  component: AiBindingContextComponentInput,
): Record<string, unknown> | undefined {
  const metadataCandidates = [
    component.metadata?.dataBinding,
    component.metadata?.binding,
    component.metadata?.bindingMetadata,
    component.metadata?.dataBindingMetadata,
    component.properties?.bindingMetadata,
    component.properties?.dataBindingMetadata,
  ];

  return metadataCandidates.find(isRecord);
}

function findSourceLabels(
  metadata: Record<string, unknown> | undefined,
  properties: Record<string, unknown> | undefined,
): CatalogSourceLabels | undefined {
  return [
    metadata?.sourceLabels,
    metadata?.catalogSourceLabels,
    properties?.sourceLabels,
    properties?.catalogSourceLabels,
  ].find(isCatalogSourceLabels);
}

function findViewLabels(
  metadata: Record<string, unknown> | undefined,
  properties: Record<string, unknown> | undefined,
): CatalogDataViewLabels | undefined {
  return [
    metadata?.viewLabels,
    metadata?.dataViewLabels,
    properties?.viewLabels,
    properties?.dataViewLabels,
  ].find(isCatalogDataViewLabels);
}

function getWriteReadiness(
  metadata: Record<string, unknown> | undefined,
  properties: Record<string, unknown> | undefined,
): Partial<Record<ComponentDataWriteOperation, string>> | undefined {
  const readinessInput = [
    metadata?.writeReadiness,
    metadata?.operationReadiness,
    properties?.writeReadiness,
    properties?.operationReadiness,
  ].find(isRecord);

  if (readinessInput === undefined) {
    return undefined;
  }

  const readiness: Partial<Record<ComponentDataWriteOperation, string>> = {};
  for (const operation of WRITE_OPERATIONS) {
    const value = readinessInput[operation];
    const parsed = parseReadinessValue(value);
    if (parsed !== undefined) {
      readiness[operation] = parsed;
    }
  }

  return Object.keys(readiness).length > 0 ? readiness : undefined;
}

function parseReadinessValue(value: unknown): string | undefined {
  if (typeof value === "boolean") {
    return value ? "ready" : "not-ready";
  }

  if (
    value === "ready" ||
    value === "not-ready" ||
    value === "missing" ||
    value === "reconnect" ||
    value === "unknown"
  ) {
    return value;
  }

  return undefined;
}

function getExplicitBindingIdentityKey(
  component: AiBindingContextComponentInput,
): string | undefined {
  const binding = parseComponentDataBinding(component.properties?.dataBinding);
  if (binding === undefined || binding.source.kind === "static") {
    return undefined;
  }

  const source = binding.source;
  if (source.kind === "schema") {
    return [
      "schema",
      source.dataOwnerOrganizationId,
      source.ownerType,
      source.ownerId,
      source.groupId,
      source.schemaId,
    ].join(":");
  }

  return [
    source.kind,
    source.ownerType,
    source.ownerId,
    source.viewOrganizationId,
    source.viewId,
  ].join(":");
}
